// Network Profiles admin panel (fn-71.2 / R4).
//
// A read-only (+ scan) view of the config-repo `network-profiles/` catalog
// (fn-68): each profile's posture (web tools / package managers / allowed
// hosts), which is the org default, and scan health. A near-EXACT mirror of
// the `mcp-servers` catalog page (`pages/mcp-servers/list.tsx`) — same UX,
// same CSS classes (`skills-*` / `config-list-*` / `sb-chip`), same thin-proxy
// BFF shape (`trpc.networkProfiles.list` + `scanConfigRepo`).
//
// Deliberate differences from the mcp-servers template (R4b / R6 / R8):
//   - `scan_status` is the 3-value `'ok' | 'invalid_config' | 'scan_failed'`
//     enum — there is NO `no_endpoint` (that is mcp-only).
//   - `web_tools` / `package_managers` are `string | null` (NOT booleans):
//     `null` renders Off ("—"); a non-null value renders an On chip carrying
//     the literal value, with the raw value surfaced in the inspect disclosure.
//   - `allowed_hosts` comes from `networking.allowed_hosts` (null/empty → "none").
//   - a `default` badge renders when `is_default`.
//   - sort options match `NetworkProfileSortBy` (name | scan_status |
//     updated_at | scanned_at | is_default).
//   - the CP admin-gates `GET /network-profiles`, so a non-admin hits a 403:
//     the page detects the upstream FORBIDDEN and renders a clear admin-only
//     message instead of the generic load-error (never crashes/blanks).

import { keepPreviousData } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { trpc } from '../../trpc-client.js';
import { useDebouncedTextFilter } from '../config-list-text-filter.js';

export type NetworkProfileScanStatus = 'ok' | 'invalid_config' | 'scan_failed';

export interface NetworkProfileNetworking {
  readonly mode: 'limited' | 'unrestricted';
  readonly allowed_hosts: readonly string[];
}

export interface NetworkProfileRow {
  readonly name: string;
  readonly is_default: boolean;
  readonly web_tools: string | null;
  readonly package_managers: string | null;
  readonly networking: NetworkProfileNetworking | null;
  readonly raw_config: string;
  readonly scan_status: NetworkProfileScanStatus;
  readonly scan_error: string | null;
  readonly scanned_at: string | null;
  readonly updated_at: string | null;
}

type NetworkProfileSortBy = 'name' | 'scan_status' | 'updated_at' | 'scanned_at' | 'is_default';
type SortDirection = 'asc' | 'desc';

export interface NetworkProfilesListInput {
  readonly limit: number;
  readonly q?: string;
  readonly sort_by: NetworkProfileSortBy;
  readonly sort_dir: SortDirection;
  readonly cursor?: string;
  readonly scan_status?: NetworkProfileScanStatus;
}

const PAGE_SIZES = [10, 25, 50, 100] as const;
const NP_SORT_OPTIONS: ReadonlyArray<{
  readonly value: NetworkProfileSortBy;
  readonly label: string;
}> = [
  { value: 'name', label: 'Name' },
  { value: 'scan_status', label: 'Status' },
  { value: 'updated_at', label: 'Updated' },
  { value: 'scanned_at', label: 'Scanned' },
  { value: 'is_default', label: 'Default' },
];
const NP_STATUS_OPTIONS: ReadonlyArray<{
  readonly value: NetworkProfileScanStatus;
  readonly label: string;
}> = [
  { value: 'ok', label: 'Active' },
  { value: 'invalid_config', label: 'Invalid config' },
  { value: 'scan_failed', label: 'Scan failed' },
];

export function formatNetworkProfileStatus(status: NetworkProfileScanStatus): string {
  switch (status) {
    case 'ok':
      return 'Active';
    case 'invalid_config':
      return 'Invalid config';
    case 'scan_failed':
      return 'Scan failed';
  }
}

/**
 * Format a `string | null` posture flag (`web_tools` / `package_managers`).
 * The CP carries the literal profile value (or `null` when unset); a non-null
 * value reads as "On" (with the literal shown in the inspect disclosure),
 * `null` reads as "Off" (R4b).
 */
export function formatPostureFlag(value: string | null): string {
  return value !== null && value.length > 0 ? 'On' : 'Off';
}

/** The neutral/positive chip class for a posture flag (On = positive, Off = neutral). */
export function postureChipClass(value: string | null): string {
  return value !== null && value.length > 0 ? 'sb-chip np-posture-on' : 'sb-chip np-posture-off';
}

/**
 * The allowed-hosts summary for the card row. `null`/empty networking → "none";
 * otherwise the comma-joined bare hostnames (R4b).
 */
export function formatAllowedHosts(networking: NetworkProfileNetworking | null): string {
  const hosts = networking?.allowed_hosts ?? [];
  return hosts.length === 0 ? 'none' : hosts.join(', ');
}

export function readNetworkProfilesListInput(params: URLSearchParams): NetworkProfilesListInput {
  const rawLimit = Number(params.get('limit') ?? '10');
  const limit = PAGE_SIZES.includes(rawLimit as (typeof PAGE_SIZES)[number]) ? rawLimit : 10;
  const sortBy = params.get('sort_by');
  const sort_by = NP_SORT_OPTIONS.some((option) => option.value === sortBy)
    ? (sortBy as NetworkProfileSortBy)
    : 'name';
  const sortDir = params.get('sort_dir');
  const sort_dir: SortDirection = sortDir === 'desc' ? 'desc' : 'asc';
  const q = normalizedParam(params, 'q');
  const cursor = normalizedParam(params, 'cursor');
  const scanStatus = params.get('scan_status');
  return {
    limit,
    sort_by,
    sort_dir,
    ...(q !== undefined ? { q } : {}),
    ...(cursor !== undefined ? { cursor } : {}),
    ...(isNetworkProfileStatus(scanStatus) ? { scan_status: scanStatus } : {}),
  };
}

function normalizedParam(params: URLSearchParams, key: string): string | undefined {
  const value = params.get(key)?.trim();
  return value === undefined || value.length === 0 ? undefined : value;
}

function isNetworkProfileStatus(value: string | null): value is NetworkProfileScanStatus {
  return NP_STATUS_OPTIONS.some((option) => option.value === value);
}

function hasActiveFilters(input: NetworkProfilesListInput): boolean {
  return input.q !== undefined || input.scan_status !== undefined || input.cursor !== undefined;
}

function formatDate(value: string | null): string {
  if (value === null) return '-';
  return new Date(value).toLocaleString();
}

/**
 * Detect the CP's PlatformAdmin **403** on the list query (R6). The CP
 * admin-gates `GET /network-profiles`, so a non-admin user hits a FORBIDDEN.
 * The BFF proxy maps the upstream 403 to a tRPC `FORBIDDEN` (httpStatus 403),
 * so we read either signal. Pure + exported so the contract is unit-testable
 * without mounting the tRPC tree.
 */
export function isForbiddenError(
  error:
    | {
        readonly data?: { readonly httpStatus?: number; readonly code?: string } | null | undefined;
      }
    | null
    | undefined,
): boolean {
  const data = error?.data;
  if (data === null || data === undefined) return false;
  return data.httpStatus === 403 || data.code === 'FORBIDDEN';
}

/**
 * Per-profile config/status inspection disclosure (R4 / R4b). Mirrors
 * `McpInspectSection`: a collapsed `<details>` revealing the FULL profile
 * projection (scan_status, the RAW posture values, networking mode + allowed
 * hosts, all timestamps, scan_error, and the raw config) read from the list
 * payload — there is no network-profiles `byId` endpoint.
 *
 * Status renders via this page's OWN `formatNetworkProfileStatus` + the
 * `np-status-<status>` chip class (NOT `StatusPill`, which understands only
 * the session statuses).
 */
export function NetworkProfileInspectSection({
  profile,
}: {
  profile: NetworkProfileRow;
}): React.ReactElement {
  return (
    <details className="skill-card-inspect">
      <summary className="skill-card-inspect-summary">Inspect config</summary>
      <dl className="skill-card-inspect-meta">
        <div>
          <dt>Status</dt>
          <dd>
            <span className={`sb-chip np-status-${profile.scan_status}`}>
              {formatNetworkProfileStatus(profile.scan_status)}
            </span>
          </dd>
        </div>
        <div>
          <dt>Default</dt>
          <dd>{profile.is_default ? 'Yes' : 'No'}</dd>
        </div>
        <div>
          <dt>Web tools</dt>
          <dd>
            {profile.web_tools !== null && profile.web_tools.length > 0 ? (
              <code>{profile.web_tools}</code>
            ) : (
              '-'
            )}
          </dd>
        </div>
        <div>
          <dt>Package managers</dt>
          <dd>
            {profile.package_managers !== null && profile.package_managers.length > 0 ? (
              <code>{profile.package_managers}</code>
            ) : (
              '-'
            )}
          </dd>
        </div>
        <div>
          <dt>Network mode</dt>
          <dd>{profile.networking !== null ? profile.networking.mode : '-'}</dd>
        </div>
        <div>
          <dt>Allowed hosts</dt>
          <dd>{formatAllowedHosts(profile.networking)}</dd>
        </div>
        <div>
          <dt>Scanned</dt>
          <dd>{formatDate(profile.scanned_at)}</dd>
        </div>
        <div>
          <dt>Updated</dt>
          <dd>{formatDate(profile.updated_at)}</dd>
        </div>
      </dl>
      {profile.scan_error !== null && profile.scan_error.length > 0 && (
        <p className="skills-row-error" role="alert">
          {profile.scan_error}
        </p>
      )}
      <span className="skill-card-inspect-label">Raw config</span>
      <pre className="mcp-raw-config">{profile.raw_config}</pre>
    </details>
  );
}

/**
 * A single network-profile card (pure / prop-driven, R4 / R4b). Split out from
 * the page so the default badge + posture chips + allowed-hosts row are
 * unit-testable without mounting the tRPC tree (mirrors the gateway-tools
 * `GatewayTargetCard` convention).
 */
export function NetworkProfileCard({
  profile,
}: {
  profile: NetworkProfileRow;
}): React.ReactElement {
  return (
    <li className="skill-card">
      <div className="skill-card-header">
        <span className="skill-card-name">{profile.name}</span>
        {profile.is_default && <span className="sb-chip np-default-badge">Default</span>}
        <span className={`sb-chip np-status-${profile.scan_status}`}>
          {formatNetworkProfileStatus(profile.scan_status)}
        </span>
      </div>

      {profile.scan_error !== null && profile.scan_error.length > 0 && (
        <div className="skill-card-description">
          <span className="skills-row-error">{profile.scan_error}</span>
        </div>
      )}

      <div className="skill-card-meta">
        <span className="skill-meta-item">
          <span className="skill-meta-label">Web tools</span>
          <span className={postureChipClass(profile.web_tools)}>
            {formatPostureFlag(profile.web_tools)}
          </span>
        </span>
        <span className="skill-meta-item">
          <span className="skill-meta-label">Package managers</span>
          <span className={postureChipClass(profile.package_managers)}>
            {formatPostureFlag(profile.package_managers)}
          </span>
        </span>
        <span className="skill-meta-item">
          <span className="skill-meta-label">Allowed hosts</span>
          <span className="skill-meta-value is-plain">
            {formatAllowedHosts(profile.networking)}
          </span>
        </span>
        <span className="skill-meta-item">
          <span className="skill-meta-label">Scanned</span>
          <span className="skill-meta-value is-plain">{formatDate(profile.scanned_at)}</span>
        </span>
      </div>

      <NetworkProfileInspectSection profile={profile} />
    </li>
  );
}

/** The shape of the `networkProfiles.scanConfigRepo` result (R5). */
export interface NetworkProfileScanResult {
  readonly scanned_at: string;
  readonly discovered_count: number;
  readonly valid_count: number;
  readonly invalid_count: number;
  readonly default_profile: string | null;
}

/**
 * The scan-result summary (pure, R5). Surfaces discovered/valid/invalid counts
 * AND the accepted catalog's `default_profile`. Split out so the
 * default-profile surfacing is unit-testable without mounting the mutation.
 */
export function ScanResultSummary({
  result,
}: {
  result: NetworkProfileScanResult;
}): React.ReactElement {
  return (
    <p className="skills-result">
      Scan found {result.discovered_count} profiles; {result.valid_count} valid,{' '}
      {result.invalid_count} invalid. Default profile:{' '}
      {result.default_profile !== null ? result.default_profile : 'none'}.
    </p>
  );
}

/**
 * The "Scan config repo" button (pure, R5/R9). Split out so the button label,
 * the disabled/scanning state, and the click callback are unit-testable without
 * mounting the page's tRPC mutation — matching the console's pure-component test
 * convention (the page tests do not mock the tRPC client).
 */
export function NetworkProfilesScanButton({
  scanning,
  onScan,
}: {
  scanning: boolean;
  onScan: () => void;
}): React.ReactElement {
  return (
    <button type="button" className="skills-scan-button" disabled={scanning} onClick={onScan}>
      {scanning ? 'Scanning...' : 'Scan config repo'}
    </button>
  );
}

export function NetworkProfilesListPage(): React.ReactElement {
  const utils = trpc.useUtils();
  const [searchParams, setSearchParams] = useSearchParams();
  const [previousCursors, setPreviousCursors] = useState<string[]>([]);
  const listInput = readNetworkProfilesListInput(searchParams);
  const networkProfilesQuery = trpc.networkProfiles.list.useQuery(listInput, {
    placeholderData: keepPreviousData,
    // 403 (non-admin) is an expected terminal state — don't auto-retry it.
    retry: false,
  });
  const scanConfigRepo = trpc.networkProfiles.scanConfigRepo.useMutation();

  const profiles = (networkProfilesQuery.data?.data ?? []) as readonly NetworkProfileRow[];
  const lastScanAt = networkProfilesQuery.data?.last_scan_at ?? null;
  const nextCursor = networkProfilesQuery.data?.next_cursor ?? null;

  const setListParam = useCallback(
    (updates: Readonly<Record<string, string | null>>, replace = true): void => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(updates)) {
        if (value === null || value.trim().length === 0) next.delete(key);
        else next.set(key, value);
      }
      if (!Object.prototype.hasOwnProperty.call(updates, 'cursor')) {
        next.delete('cursor');
        setPreviousCursors([]);
      }
      setSearchParams(next, { replace });
    },
    [searchParams, setSearchParams],
  );

  const commitSearchFilter = useCallback(
    (value: string): void => setListParam({ q: value }),
    [setListParam],
  );
  const [searchDraft, setSearchDraft] = useDebouncedTextFilter(listInput.q, commitSearchFilter);

  const clearCursorForScan = (): void => {
    setPreviousCursors([]);
    // Use the functional updater so we drop `cursor` from the LATEST params at
    // completion time — a scan is async, and reading the click-time `searchParams`
    // snapshot would clobber any search/filter/sort the user changed mid-scan.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('cursor');
        return next;
      },
      { replace: true },
    );
  };

  if (networkProfilesQuery.isPending) {
    return (
      <section className="page-network-profiles" aria-busy="true">
        <h1>Network Profiles</h1>
        <p>Loading network profiles...</p>
      </section>
    );
  }

  if (networkProfilesQuery.error !== null) {
    // R6 — the CP admin-gates GET /network-profiles. A non-admin hits a 403:
    // render a clear admin-only message rather than the generic load-error
    // (never a redirect — AuthGate already confirmed the session — and never
    // a crash/blank).
    if (isForbiddenError(networkProfilesQuery.error)) {
      return (
        <section className="page-network-profiles" role="alert">
          <h1>Network Profiles</h1>
          <p>You don't have access — network profiles are admin-only.</p>
        </section>
      );
    }
    return (
      <section className="page-network-profiles" role="alert">
        <h1>Network Profiles</h1>
        <p>Failed to load network profiles: {networkProfilesQuery.error.message}</p>
      </section>
    );
  }

  return (
    <section className="page-network-profiles">
      <div className="skills-header">
        <div>
          <h1>Network Profiles</h1>
          <p className="skills-header-meta">Last scanned: {formatDate(lastScanAt)}</p>
        </div>
        <NetworkProfilesScanButton
          scanning={scanConfigRepo.isPending}
          onScan={() => {
            void scanConfigRepo
              .mutateAsync(undefined)
              .then(async () => {
                clearCursorForScan();
                await utils.networkProfiles.list.invalidate();
              })
              .catch(() => {});
          }}
        />
      </div>

      <div className="config-list-controls" aria-label="Network profile list controls">
        <label>
          <span>Search</span>
          <input
            type="search"
            value={searchDraft}
            aria-label="Search network profiles"
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </label>
        <label>
          <span>Status</span>
          <select
            value={listInput.scan_status ?? ''}
            aria-label="Filter network profiles by status"
            onChange={(event) => setListParam({ scan_status: event.target.value })}
          >
            <option value="">Any status</option>
            {NP_STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Sort</span>
          <select
            value={listInput.sort_by}
            aria-label="Sort network profiles by"
            onChange={(event) => setListParam({ sort_by: event.target.value })}
          >
            {NP_SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Direction</span>
          <select
            value={listInput.sort_dir}
            aria-label="Sort network profiles direction"
            onChange={(event) => setListParam({ sort_dir: event.target.value })}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </label>
        <label>
          <span>Page size</span>
          <select
            value={String(listInput.limit)}
            aria-label="Network profiles page size"
            onChange={(event) => setListParam({ limit: event.target.value })}
          >
            {PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {listInput.scan_status !== undefined && (
        <div className="config-list-filter-chips" aria-label="Active network profile filters">
          <button
            type="button"
            className="sb-chip"
            onClick={() => setListParam({ scan_status: null })}
          >
            Status: {formatNetworkProfileStatus(listInput.scan_status)} x
          </button>
        </div>
      )}

      {profiles.length === 0 ? (
        <p className="skills-empty">
          {hasActiveFilters(listInput)
            ? 'No network profiles match current filters.'
            : 'No network profiles scanned yet.'}
        </p>
      ) : (
        <ul className="skills-list" aria-label="Network Profiles">
          {profiles.map((profile) => (
            <NetworkProfileCard key={profile.name} profile={profile} />
          ))}
        </ul>
      )}

      <div className="config-list-pagination" aria-label="Network profiles pagination">
        <button
          type="button"
          disabled={previousCursors.length === 0}
          onClick={() => {
            const previous = previousCursors[previousCursors.length - 1] ?? '';
            setPreviousCursors((stack) => stack.slice(0, -1));
            setListParam({ cursor: previous.length > 0 ? previous : null }, false);
          }}
        >
          Previous
        </button>
        <button
          type="button"
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor === null) return;
            setPreviousCursors((stack) => [...stack, listInput.cursor ?? '']);
            setListParam({ cursor: nextCursor }, false);
          }}
        >
          Next
        </button>
      </div>

      {scanConfigRepo.data !== undefined && <ScanResultSummary result={scanConfigRepo.data} />}
      {scanConfigRepo.error !== null && (
        <p role="alert" className="skills-error">
          Failed to scan config repo: {scanConfigRepo.error.message}
        </p>
      )}
    </section>
  );
}
